import { makeAutoObservable, runInAction, reaction, observable, action, toJS } from 'mobx';
import { Sheet, Column, ColumnTypes } from '../models/SheetModel';
import { v4 as uuidv4 } from 'uuid';
import { CellFormatHelper } from '../helpers/CellFormatHelper';
import { CellValidationHelper } from '../helpers/CellValidationHelper';
import CellOutlineLevelHelper from '../helpers/CellOutlineLevelHelper';
import { SheetStyleHelper } from '../helpers/SheetStyleHelper';
import { ConditionalFormattingHelper } from '../helpers/ConditionalFormattingHelper'; // Import the helper
// Import or define font constants if needed for calculation logic
// For simplicity, we'll use hardcoded factors below, assuming Arial 10

// --- Constants for Width Calculation ---
const CHAR_WIDTH_FACTOR_NORMAL = 0.9; // Approx avg char width factor for Arial 10
const CHAR_WIDTH_FACTOR_BOLD = 0.95; // Bold is slightly wider
const COL_WIDTH_PADDING = 1.4; // Extra padding units (adjust as needed)
const MIN_COL_WIDTH = 13; // Minimum width in Excel units
const MAX_COL_WIDTH = 60; // Maximum width in Excel units
const INDENT_WIDTH_FACTOR = 1.5; // Approx width units per indent level (adjust as needed)
// --- End Constants ---


export class SheetDataStore {
    currentSheet = null;
    isLoading = false;
    error = null;
    modifiedSheetNames = observable(new Set()); // Track modified sheets

    constructor(rootStore) {
        if (!rootStore) {
            throw new Error('RootStore is required for SheetDataStore');
        }
        
        this.rootStore = rootStore;
        
        // Get the service from workbookStore instead
        this.service = rootStore.workbookStore.currentWorkbook;
        if (!this.service) {
            console.warn('No workbook available yet in SheetDataStore');
        }
        
        makeAutoObservable(this, {
            rootStore: false,
            service: false,
            modifiedSheetNames: observable, // Make the set observable
            markSheetAsModified: action, // Mark methods as actions
            clearModifiedFlags: action
        });

        // React to workbook changes
        reaction(
            () => this.rootStore.workbookStore.currentWorkbook,
            (workbook) => {
                console.log('SheetDataStore: Workbook changed:', workbook?.filename);
                this.service = workbook;
                // Potentially reload sheet data if the workbook changes significantly
                // Or handle sheet selection logic elsewhere
            }
        );
    }

    // --- Modification Tracking ---
    markSheetAsModified(sheetName) {
        // --- DEBUGGING START ---
        console.log(`[SheetDataStore Debug] Attempting to mark sheet "${sheetName}" as modified. Current isLoading state: ${this.isLoading}`);
        console.trace(`[SheetDataStore Debug] Call stack for markSheetAsModified:`);
        // --- DEBUGGING END ---
        if (sheetName && !this.modifiedSheetNames.has(sheetName)) {
            console.log(`[SheetDataStore] Marking sheet "${sheetName}" as modified.`);
            this.modifiedSheetNames.add(sheetName);
            // Also mark the file system as unsaved
            this.rootStore.fileSystemStore.markUnsaved();
        }
    }

    clearModifiedFlags() {
        if (this.modifiedSheetNames.size > 0) {
            console.log('[SheetDataStore] Clearing modified sheet flags.');
            this.modifiedSheetNames.clear();
        }
    }
    // --- End Modification Tracking ---

    async loadSheet(sheetName, options = { validateOutlineLevels: false }) {
        console.log('SheetDataStore: Start loading sheet:', sheetName);
        
        if (!this.service) {
            console.error('No workbook available to load sheet');
            runInAction(() => {
                this.error = new Error('No workbook loaded.');
                this.isLoading = false;
            });
            return;
        }

        runInAction(() => {
            this.isLoading = true;
            this.error = null;
            this.currentSheet = null; // Clear previous sheet
        });

        try {
            const sheet = new Sheet(sheetName);
            const worksheet = this.service.getWorksheet(sheetName);
            
            if (!worksheet) {
                 throw new Error(`Worksheet "${sheetName}" not found in the workbook.`);
            }

            runInAction(() => {
                // Get headers and set up columns with validation
                const headerRow = worksheet.getRow(1);
                const columns = [];
                
                // Determine the actual number of columns based on header or data
                const colCount = Math.max(headerRow.cellCount, worksheet.actualColumnCount || 0);

                for (let colNumber = 1; colNumber <= colCount; colNumber++) {
                    const cell = headerRow.getCell(colNumber);
                    const heading = cell.value?.toString() || `Column ${colNumber}`;
                    const columnType = this._getColumnTypeFromCell(cell); // Reads type without writing
                    
                    // --- MODIFICATION START: Always Scan Custom Validation Columns ---
                    let validationOptions = []; // Initialize empty

                    // If the column type expects custom validation, ALWAYS scan the column
                    // for unique values to ensure the model has the complete list based on content.
                    if (columnType.hasCustomValidation) {
                        console.log(`[LoadSheet] Scanning column "${heading}" for unique validation options (Type: ${columnType.id})...`);
                        const uniqueValues = new Set();
                        for (let i = 2; i <= worksheet.rowCount; i++) { // Start from data row 2
                            const dataCell = worksheet.getCell(i, colNumber);
                            let cellValue = dataCell.value;

                            // Extract text from rich text if necessary
                            if (cellValue && typeof cellValue === 'object' && cellValue.richText) {
                                cellValue = cellValue.richText.map(part => part.text).join('');
                            }

                            // Convert to string, trim, and add if not empty
                            const stringValue = String(cellValue ?? '').trim();
                            if (stringValue) {
                                uniqueValues.add(stringValue);
                            }
                        }
                        validationOptions = Array.from(uniqueValues);
                        console.log(`[LoadSheet] Found unique values for "${heading}":`, validationOptions);
                    }
                    // We no longer try to parse from the first cell's rule first.
                    // The scan result is now the definitive source for the model.
                    // --- MODIFICATION END ---

                    // Get column width from Excel
                    const col = worksheet.getColumn(colNumber);
                    const width = col.width || 10; // Default width if not specified

                    // Create the column model with the determined type and scanned options
                    const column = new Column(heading, columnType, validationOptions, width);
                    columns.push(column);

                    // --- NEW: Read Conditional Formatting ---
                    // Read existing conditional formatting for this column if applicable
                    if (column.type.hasCustomValidation) {
                         ConditionalFormattingHelper.readConditionalFormatting(worksheet, column, colNumber);
                    }
                    // --- END NEW ---
                }

                // Now set the columns to the sheet
                sheet.columns = columns;

                // --- Populate Rows ---
                const loadedRows = new Map();
                // Skip header row (row 1)
                for (let i = 2; i <= worksheet.rowCount; i++) {
                    const row = worksheet.getRow(i);
                    const rowId = uuidv4(); // Generate a new ID for the loaded row
                    const rowData = { _id: rowId, _outlineLevel: row.outlineLevel || 0 };

                    sheet.columns.forEach((column, index) => {
                        const cell = row.getCell(index + 1);
                        let rawValue = cell.value;
                        let processedValue = null;

                        // --- Value processing logic (Rich Text, Dates, Links, etc.) ---
                        if (rawValue !== null && rawValue !== undefined) {
                            if (column.type?.supportsRichText) {
                                // ... (Rich text handling logic) ...
                                let richTextArray = null;
                                if (typeof rawValue === 'object' && rawValue.richText && Array.isArray(rawValue.richText)) {
                                    richTextArray = rawValue.richText;
                                } else if (typeof rawValue === 'string') {
                                    if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
                                        try {
                                            const parsed = JSON.parse(rawValue);
                                            richTextArray = Array.isArray(parsed) ? parsed : [{ text: rawValue }];
                                        } catch (e) { richTextArray = [{ text: rawValue }]; }
                                    } else { richTextArray = [{ text: rawValue }]; }
                                } else {
                                    console.warn(`[LoadSheet] Unexpected type for Rich Text cell (${i}, ${index + 1}), wrapping:`, rawValue);
                                    richTextArray = [{ text: String(rawValue) }];
                                }
                                processedValue = (richTextArray && richTextArray.some(part => part.text && String(part.text).trim() !== '')) ? richTextArray : null;

                            } else if ((column.type.id === 'DATE' || column.type.id === 'START_DATE' || column.type.id === 'END_DATE') && rawValue instanceof Date) {
                                processedValue = rawValue;
                            } else if ((column.type.id === 'URL' || column.type.id === 'EMAIL') && typeof rawValue === 'object' && rawValue.text && rawValue.hyperlink) {
                                 processedValue = rawValue;
                            } else {
                                processedValue = rawValue;
                            }
                        }
                        // --- End Value Processing ---

                        // --- ADDITION START: Default Checkbox to False --- 
                        if (column.type.id === ColumnTypes.CHECKBOX.id && (processedValue === null || processedValue === undefined)) {
                            processedValue = false;
                        }
                        // --- ADDITION END ---

                        // Use column.id as the key
                        rowData[column.id] = {
                            value: processedValue,
                            type: column.type,
                            properties: {}
                        };
                    });
                    loadedRows.set(rowId, rowData);
                }

                // Assign loadedRows to sheet.rows, set currentSheet
                this.currentSheet = sheet;
                this.currentSheet.rows = loadedRows;

                // Remove from modified set as loading resets the state
                this.modifiedSheetNames.delete(sheetName);

                console.log(`Sheet "${sheetName}" loaded successfully with ${loadedRows.size} rows.`);

            }); // end runInAction

            // Only validate outline levels if explicitly requested
            if (options.validateOutlineLevels) {
                await this.validateOutlineLevels(options);
            }
        } catch (error) {
            console.error('Error loading sheet:', error);
            runInAction(() => {
                this.error = error;
                this.currentSheet = null; // Ensure sheet is null on error
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
            console.log('SheetDataStore: Finished loading sheet:', sheetName, 'Success:', !!this.currentSheet);
        }
    }

    // Private helpers
    _getSheetData() {
        return {
            columns: this.currentSheet.columns,
            rows: Array.from(this.currentSheet.rows.values())
        };
    }

    async saveChanges() {
        if (!this.service || !this.currentSheet) {
            console.warn('[SaveChanges] Cannot save changes: No service or current sheet.');
            return;
        }

        try {
            const worksheet = this.service.getWorksheet(this.currentSheet.name);
            if (!worksheet) {
                console.warn(`[SaveChanges] Worksheet not found: ${this.currentSheet.name}`);
                return;
            }

            console.log('[SaveChanges] Syncing data for sheet:', this.currentSheet.name);
            // --- Set Values --- // Renamed section slightly
            const rowIds = Array.from(this.currentSheet.rows.keys());
            const columns = this.currentSheet.columns;

            // Ensure worksheet has enough rows (consider headers)
            const requiredRowCount = rowIds.length + 1;
            while (worksheet.rowCount < requiredRowCount) {
                worksheet.addRow({}); // Add empty rows if needed
            }

            rowIds.forEach((rowId, rowIndex) => {
                const rowData = this.currentSheet.rows.get(rowId);
                const excelRowIndex = rowIndex + 2; // +1 for 0-based index, +1 for header row
                const row = worksheet.getRow(excelRowIndex);

                columns.forEach((column, colIndex) => {
                    const excelColIndex = colIndex + 1;
                    const cell = row.getCell(excelColIndex);
                    // Access cell data using column.id
                    const cellData = rowData[column.id];
                    const value = cellData?.value; // Use optional chaining

                    // --- RICH TEXT / VALUE FORMATTING LOGIC (Prepare value for ExcelJS)---
                    let valueToSet = null;
                    if (value !== null && value !== undefined) {
                        if (column?.type?.supportsRichText) {
                            if (Array.isArray(value)) {
                                valueToSet = { richText: value };
                            } else if (typeof value === 'object' && value.richText) {
                                valueToSet = value;
                            } else if (typeof value === 'string') {
                                valueToSet = { richText: [{ text: value }] };
                            } else {
                                console.warn(`[SaveChanges] Unexpected value type for Rich Text supporting column ${column.heading} in row ${rowId}: ${typeof value}`, value);
                                valueToSet = null;
                            }
                        } else if (column?.type?.id === 'DATE' || column?.type?.id === 'START_DATE' || column?.type?.id === 'END_DATE') {
                            if (typeof value === 'object' && value.date) {
                                if (value.isDateOnly) {
                                    const [year, month, day] = value.date.split('-').map(Number);
                                    valueToSet = new Date(Date.UTC(year, month - 1, day));
                                } else {
                                    valueToSet = new Date(value.date);
                                }
                            } else if (value instanceof Date) {
                                valueToSet = value;
                            } else {
                                valueToSet = null;
                            }
                        } else if (column?.type?.id === 'URL' || column?.type?.id === 'EMAIL') {
                             if (typeof value === 'object' && value.hyperlink) {
                                valueToSet = { text: value.text, hyperlink: value.hyperlink };
                             } else if (typeof value === 'string') {
                                 valueToSet = { text: value, hyperlink: value };
                             } else {
                                 valueToSet = null;
                             }
                        } else {
                            valueToSet = value;
                        }
                    }
                    // --- END VALUE FORMATTING ---

                    // --- DEBUG LOGGING ---
                    if (column?.type?.supportsRichText && valueToSet !== null) {
                        // console.log(`[SaveChanges Debug] Setting Rich Text for ${cell.address}:`, JSON.stringify(valueToSet), typeof valueToSet);
                    }
                    // --- END DEBUG LOGGING ---

                    cell.value = valueToSet;

                    // REMOVED: Validation application logic moved to dedicated section below
                });
                 // REMOVED: Outline level setting moved to SheetStyleHelper
            });
            console.log('[SaveChanges] Data value sync complete for sheet:', this.currentSheet.name);

            // --- Calculate Column Widths Based on Content ---
            console.log('[SaveChanges] Calculating column widths for:', this.currentSheet.name);
            runInAction(() => {
                let maxOutlineLevel = 0;
                if (this.currentSheet.columns.length > 0) {
                    this.currentSheet.rows.forEach(rowData => {
                        const level = rowData._outlineLevel || 0;
                        if (level > maxOutlineLevel) {
                            maxOutlineLevel = level;
                        }
                    });
                }
                const firstColIndentationPadding = (maxOutlineLevel + 1) * INDENT_WIDTH_FACTOR;

                this.currentSheet.columns.forEach((column, colIndex) => {
                    let maxContentWidth = 0;
                    const isFirstCol = colIndex === 0;

                    const headerWidth = SheetDataStore._calculateExcelWidth(column.heading, true);
                    maxContentWidth = Math.max(maxContentWidth, headerWidth);

                    this.currentSheet.rows.forEach((rowData) => {
                        // Access cell data using column.id
                        const cellData = rowData[column.id];
                        const displayValue = this._getCellDisplayValue(cellData, column.type);
                        const isBold = isFirstCol;
                        const cellWidth = SheetDataStore._calculateExcelWidth(displayValue, isBold);
                        maxContentWidth = Math.max(maxContentWidth, cellWidth);
                    });

                    if (isFirstCol) {
                        maxContentWidth += firstColIndentationPadding;
                    }

                    const finalWidth = Math.max(MIN_COL_WIDTH, Math.min(maxContentWidth, MAX_COL_WIDTH));

                    if (column.width !== finalWidth) {
                        column.width = finalWidth;
                    }
                });
                 this.markSheetAsModified(this.currentSheet?.name); // Width changes mark modified
            });
            console.log('[SaveChanges] Column width calculation complete.');
            // --- End Width Calculation ---

            // --- MODIFICATION START: Explicitly Apply Data Validations ---
            console.log('[SaveChanges] Applying data validations for:', this.currentSheet.name);
            columns.forEach((column, colIndex) => {
                const excelColIndex = colIndex + 1;

                // Apply list validation if applicable based on model data
                if (column.type.hasCustomValidation && column.validationOptions && column.validationOptions.length > 0) {
                    const validationRule = {
                        type: 'list',
                        allowBlank: true,
                        formulae: [`"${column.validationOptions.join(',')}"`] // Use options from model
                    };
                    // Apply to all data rows
                    for (let rowIndex = 0; rowIndex < rowIds.length; rowIndex++) {
                        const excelRowIndex = rowIndex + 2;
                        const cell = worksheet.getCell(excelRowIndex, excelColIndex);
                        cell.dataValidation = validationRule;
                    }
                    console.log(`[SaveChanges] Applied list validation to column "${column.heading}" with options:`, column.validationOptions);
                } else {
                    // Optional: Clear list validation if type changed or options removed
                    // Iterate through rows and clear ONLY if existing validation is 'list'
                    for (let rowIndex = 0; rowIndex < rowIds.length; rowIndex++) {
                        const excelRowIndex = rowIndex + 2;
                        const cell = worksheet.getCell(excelRowIndex, excelColIndex);
                        if (cell.dataValidation?.type === 'list') {
                            cell.dataValidation = undefined; // Clear list validation
                            // console.log(`[SaveChanges] Cleared list validation from column "${column.heading}" cell ${cell.address}`);
                        }
                        // Note: We are NOT clearing other validation types like 'date' or 'number' here.
                        // Those should be handled by type change logic or potentially SheetStyleHelper if needed.
                    }
                }
            });
            console.log('[SaveChanges] Data validation application complete.');
            // --- MODIFICATION END ---


            // --- Apply Styling using the Helper ---
            // This now uses the column.width values calculated above and applies other styles/formats
            // It will also handle outline levels.

            SheetStyleHelper.applySheetStyles(worksheet, this.currentSheet);

            // --- NEW: Apply Conditional Formatting ---
            console.log('[SaveChanges] Applying conditional formatting for:', this.currentSheet.name);
            const startRow = 2; // Data starts at row 2
            const endRow = this.currentSheet.rows.size + 1; // Last data row + 1 for header offset
            if (startRow <= endRow) { // Only apply if there are data rows
                this.currentSheet.columns.forEach((column, colIndex) => {
                    const excelColIndex = colIndex + 1;
                    // Apply formatting for any column that supports it
                    if (column.type.hasConditionalFormatting) {
                        // Always clear first (handled inside applyConditionalFormatting)
                        ConditionalFormattingHelper.applyConditionalFormatting(
                            worksheet,
                            column,
                            excelColIndex,
                            startRow,
                            endRow
                        );
                    } else {
                        // For columns that don't support CF, ensure we clear any existing rules
                        ConditionalFormattingHelper.clearConditionalFormatting(
                            worksheet,
                            excelColIndex,
                            startRow,
                            endRow
                        );
                    }
                });
            } else {
                console.log('[SaveChanges] Skipping conditional formatting application (no data rows).');
            }
            console.log('[SaveChanges] Conditional formatting application complete.');
            // --- END NEW ---

        } catch (error) {
            console.error(`Error syncing data for sheet ${this.currentSheet?.name}:`, error);
            // Consider re-throwing or setting an error state
        }
    }

    /***************************************
     *              COLUMNS                *
     ***************************************/

    /**
     * Column operations include:
     * - Adding columns
     * - Deleting columns 
     * - Moving columns
     * - Updating column properties
     */

    // Column width settings
    static columnWidthMultiplier = 9.4899169632;
    static defaultColumnWidth = 8.43;
    static minColumnWidth = 4;
    static maxColumnWidth = 30;

    /**
     * @returns {Promise<Column>} The newly created column object.
     */
    async addColumn(heading, type = ColumnTypes.SINGLE_LINE_TEXT, options = { relativeTo: null, position: 'right' }) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        console.log(`[SheetDataStore.addColumn] START - Heading: "${heading}", Type: ${type.id}, Options:`, options); // Log input

        const newColumn = new Column(heading, type, [], this.defaultColumnWidth);
        let insertIndex;

        runInAction(() => {
            console.log(`[SheetDataStore.addColumn] runInAction START - Initial column count: ${this.currentSheet.columns.length}`); // Log initial count
            const originalColumnCount = this.currentSheet.columns.length;
            const originalRowCount = this.currentSheet.rows.size;

            if (options.relativeTo) {
                console.log(`[SheetDataStore.addColumn] Path: Relative insertion`); // Log path
                const currentIndex = this.currentSheet.columns.findIndex(col => col.id === options.relativeTo);
                if (currentIndex === -1) {
                    throw new Error('Reference column not found');
                }

                insertIndex = options.position === 'left' ? currentIndex : currentIndex + 1;
                console.log(`[SheetDataStore.addColumn] Calculated insertIndex (0-based): ${insertIndex}`); // Log calculated index

                // --- NEW: Clear CF for columns being shifted ---
                console.log(`[AddColumn Relative CF] Clearing CF for columns from index ${insertIndex} onwards`);
                const startRowCF = 2;
                const endRowCF = this.currentSheet.rows.size + 1;
                if (startRowCF <= endRowCF) {
                    for (let i = insertIndex; i < this.currentSheet.columns.length; i++) {
                        // No need to check type, just clear for the range
                        ConditionalFormattingHelper.clearConditionalFormatting(
                            worksheet,
                            i + 1, // Excel index
                            startRowCF,
                            endRowCF
                        );
                    }
                }
                // --- END NEW ---

                // 1. Update local model first
                const columns = [...this.currentSheet.columns];
                console.log(`[SheetDataStore.addColumn] Model update: Splicing new column at index ${insertIndex}`);
                columns.splice(insertIndex, 0, newColumn);
                this.currentSheet.columns = columns;
                console.log(`[SheetDataStore.addColumn] Model updated. New column count: ${this.currentSheet.columns.length}`); // Log count after model update


                // 2. Handle Excel worksheet updates and validations (only once)
                console.log(`[SheetDataStore.addColumn] Calling CellValidationHelper.handleColumnInsertion with insertIndex: ${insertIndex}`);
                // Assuming CellValidationHelper.handleColumnInsertion updates worksheet structure
                 CellValidationHelper.handleColumnInsertion(
                     worksheet,
                     insertIndex, // Pass 0-based index
                     this.currentSheet.columns,
                     newColumn
                 );
                console.log(`[SheetDataStore.addColumn] Returned from CellValidationHelper.handleColumnInsertion. Worksheet actualColumnCount: ${worksheet.actualColumnCount}`); // Log worksheet state


                // 3. Set up validations for the new column (Excel)
                // This might be redundant if handleColumnInsertion does it? Check helper.
                // Let's assume it's needed for now.
                console.log(`[SheetDataStore.addColumn] Calling CellValidationHelper.setupNewColumnValidation with Excel index: ${insertIndex + 1}`);
                CellValidationHelper.setupNewColumnValidation(
                    worksheet,
                    insertIndex + 1, // Pass 1-based Excel index
                    type,
                    type.defaultOptions || []
                );
                console.log(`[SheetDataStore.addColumn] Returned from CellValidationHelper.setupNewColumnValidation`);


                // 4. Update local row data with preserved types
                  console.log(`[SheetDataStore.addColumn] Starting row data update loop (rows: ${originalRowCount})`);
                this.currentSheet.rows.forEach((rowData, rowId) => {
                     // Define new cell data
                     const newCellData = {
                         value: newColumn.type.defaultValue !== undefined ? newColumn.type.defaultValue : null,
                         type: newColumn.type.id,
                         properties: {}
                     };

                     // Create an intermediate object with the new cell data added
                     // Ensure we preserve the existing _outlineLevel
                     const updatedRowDataWithNewCol = { ...rowData, [newColumn.id]: newCellData };

                     // Rebuild the row data object based on the *new* column order to ensure consistency
                     // This is important because the order of keys might matter for some consumers,
                     // and ensures only data for current columns is included.
                     const finalUpdatedRowData = { _outlineLevel: updatedRowDataWithNewCol._outlineLevel || 0 };
                     this.currentSheet.columns.forEach(column => {
                         // Only copy data if it exists in the intermediate object (handles potential edge cases)
                         if (updatedRowDataWithNewCol[column.id] !== undefined) {
                              finalUpdatedRowData[column.id] = updatedRowDataWithNewCol[column.id]; // Copy data using column.id as key
                         } else {
                              // If for some reason data is missing (shouldn't happen here), initialize default
                              finalUpdatedRowData[column.id] = { value: null, type: column.type, properties: {} };
                         }
                     });

                     // Replace the existing row data in the map with the fully rebuilt object
                     this.currentSheet.rows.set(rowId, finalUpdatedRowData);
                });
                  console.log(`[SheetDataStore.addColumn] Finished row data update loop`);

                // --- NEW: Explicitly re-apply CF for new column IF it supports it --- 
                const newColumnModel = this.currentSheet.columns[insertIndex]; // Get model at insert index
                if (newColumnModel && newColumnModel.type.hasConditionalFormatting && startRowCF <= endRowCF) {
                     console.log(`[AddColumn Relative CF] Explicitly applying CF for new column "${newColumnModel.heading}" at index ${insertIndex + 1}`);
                     this._reapplyColumnConditionalFormatting(worksheet, newColumnModel, insertIndex + 1);
                }
                // --- END NEW ---

                // --- NEW: Re-apply all CF after structural changes --- 
                // This is outside runInAction as it might be async internally
                console.log('[AddColumn Relative CF] Reapplying CF for all columns after insertion');
                this._reapplyConditionalFormattingForAllColumns(worksheet);
                // --- END NEW ---
            } else {
                // Add to end
                console.log(`[SheetDataStore.addColumn] Path: Append to end`); // Log path
                insertIndex = this.currentSheet.columns.length; // Index before adding (0-based)
                console.log(`[SheetDataStore.addColumn] Calculated insertIndex (0-based): ${insertIndex}`); // Log calculated index
                console.log(`[SheetDataStore.addColumn] Model update: Appending new column`);
                this.currentSheet.columns = [...this.currentSheet.columns, newColumn];
                console.log(`[SheetDataStore.addColumn] Model updated. New column count: ${this.currentSheet.columns.length}`); // Log count after model update


                // Add header cell and set its type validation (Excel)
                console.log(`[SheetDataStore.addColumn] Setting header cell (Excel index: ${insertIndex + 1})`);
                const headerCell = worksheet.getCell(1, insertIndex + 1); // Use 1-based Excel index
                headerCell.value = heading;
                this._setColumnTypeValidation(headerCell, type); // Set header type label
                console.log(`[SheetDataStore.addColumn] Header cell set (Excel)`);

                // --- MODIFICATION START: Set data validation --- (Excel)
                console.log(`[SheetDataStore.addColumn] Setting data validation (Excel index: ${insertIndex + 1})`);
                if (type.id === ColumnTypes.CHECKBOX.id) {
                    const checkboxValidationRule = {
                        type: 'list', allowBlank: true, formulae: ['"TRUE,FALSE"']
                    };
                    this.applyColumnValidation(worksheet, insertIndex + 1, checkboxValidationRule, true); // Use helper
                } else if (type.hasCustomValidation) {
                    // Apply default list validation if applicable
                     const validationRule = CellValidationHelper.createOptionsValidation(
                         type.validationOptions || []
                     );
                     this.applyColumnValidation(worksheet, insertIndex + 1, validationRule, true); // Use helper
                }
                console.log(`[SheetDataStore.addColumn] Data validation set (Excel). Worksheet actualColumnCount: ${worksheet.actualColumnCount}`); // Log worksheet state
                // --- MODIFICATION END ---


                // Initialize empty cells for all existing rows in the model
                console.log(`[SheetDataStore.addColumn] Starting row data update loop (rows: ${originalRowCount})`);
                this.currentSheet.rows.forEach((rowData, rowId) => {
                    // Access using column.id
                    // --- MODIFICATION START: Define newCellData first ---
                    const newCellData = {
                        // --- MODIFICATION: Use defined default value, considering CHECKBOX ---
                        value: newColumn.type.defaultValue !== undefined ? newColumn.type.defaultValue : null, // Use newColumn.type
                        // --- END MODIFICATION ---
                        type: newColumn.type.id, // Use newColumn.type
                        properties: {} // Add properties object
                    };
                    // --- MODIFICATION END ---

                    // --- MODIFICATION START: Replace row object instead of mutating ---
                    // Create a new object incorporating the new cell data
                    const updatedRowData = { ...rowData, [newColumn.id]: newCellData };
                    // Replace the existing row data in the map
                    this.currentSheet.rows.set(rowId, updatedRowData);
                    // --- MODIFICATION END ---
                });
                console.log(`[SheetDataStore.addColumn] Finished row data update loop`);
            }
            console.log(`[SheetDataStore.addColumn] runInAction END - Final column count: ${this.currentSheet.columns.length}`); // Log final model count
        }); // End runInAction

        // Force workbook update to ensure changes are persisted
        this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
        console.log(`[SheetDataStore.addColumn] END - Returning newColumn. Final worksheet actualColumnCount: ${worksheet?.actualColumnCount}`); // Log final worksheet count
        return newColumn;
    }

    async deleteColumn(columnId) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const column = this.currentSheet.columns.find(col => col.id === columnId);
        if (!column) return;

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        const columnIndex = this.currentSheet.columns.indexOf(column);
        const columnSupportsCf = column.type.hasCustomValidation; // Check before deletion

        runInAction(() => {
            // --- EDIT: Moved range calculation inside runInAction ---
            const startRow = 2;
            // Get row count *before* modifying columns/rows for accurate range
            const endRow = this.currentSheet.rows.size + 1;
            // --- END EDIT ---

            // --- Clear Conditional Formatting BEFORE deleting column ---
            if (columnSupportsCf && startRow <= endRow) {
                console.log(`[DeleteColumn CF] Clearing CF for deleted column index ${columnIndex + 1}`);
                ConditionalFormattingHelper.clearConditionalFormatting(
                    worksheet,
                    columnIndex + 1, // Excel is 1-based
                    startRow,
                    endRow
                );
            }

            // 1. Handle Excel worksheet updates first
            CellValidationHelper.handleColumnDeletionComplete(
                worksheet,
                columnIndex + 1,  // Excel is 1-based
                this.currentSheet.columns.length
            );

            // 2. Update local model
            this.currentSheet.columns = this.currentSheet.columns.filter(col => col.id !== columnId);

            // 3. Update row data
            this.currentSheet.rows.forEach((rowData, rowId) => {
                const updatedRowData = {};

                // --- EDIT: Explicitly handle _outlineLevel and column data ---
                Object.keys(rowData).forEach(key => {
                    if (key === '_outlineLevel') {
                        // Copy outline level directly (it's a number)
                        updatedRowData[key] = rowData[key];
                    } else if (key !== column.heading) { // Skip the deleted column's heading
                        // Copy other column data using spread (it's an object)
                        // Ensure the data exists and is an object before spreading
                        if (rowData[key] !== null && typeof rowData[key] === 'object') {
                             updatedRowData[key] = { ...rowData[key] };
                        } else {
                             // Handle cases where data might be missing or primitive (though unlikely for column data)
                             // If it's not the outline level and not the deleted column, copy it as is.
                             updatedRowData[key] = rowData[key];
                        }
                    }
                    // If key === column.heading, do nothing (effectively deleting it)
                });
                // --- END EDIT ---

                // Replace the entire row data
                this.currentSheet.rows.set(rowId, updatedRowData);
            });
        });

        // --- MODIFICATION: Re-apply ALL CF after structural changes --- 
        console.log('[DeleteColumn CF] Reapplying CF for all columns after deletion');
        console.log('[MoveColumn CF] Reapplying CF for all columns after move completion'); // Correct log text
        await this._reapplyConditionalFormattingForAllColumns(worksheet); 
        // --- END MODIFICATION ---

        this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    async updateColumnHeading(columnId, newHeading) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const column = this.currentSheet.columns.find(col => col.id === columnId);
        if (!column) return;

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        const columnIndex = this.currentSheet.columns.indexOf(column) + 1;

        runInAction(() => {
            // Update local model
            const oldHeading = column.heading;
            column.heading = newHeading;

            // Update all row data with new heading
            this.currentSheet.rows.forEach(rowData => {
                if (rowData[oldHeading]) {
                    // Preserve formatting when updating heading
                    rowData[newHeading] = {
                        ...rowData[oldHeading],
                        type: column.type
                    };
                    delete rowData[oldHeading];
                }
            });

            // Update Excel worksheet
            const headerCell = worksheet.getCell(1, columnIndex);
            headerCell.value = newHeading;
            
            // Ensure header cell has standard formatting
            headerCell.alignment = {
                wrapText: false,
                vertical: 'bottom'
            };
            headerCell.font = {
                bold: true, // Headers are typically bold
                italic: false,
                underline: false
            };
        });

        // this.rootStore.sheetStore._forceWorkbookUpdate();
        this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    async updateColumnType(columnId, newType) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const column = this.currentSheet.columns.find(col => col.id === columnId);
        if (!column) return;

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        const columnIndex = this.currentSheet.columns.indexOf(column) + 1;
        const headerCell = worksheet.getCell(1, columnIndex);
        // --- EDIT 1: Store old type info ---
        const oldType = column.type;
        const oldTypeSupportsCustomValidation = oldType?.hasCustomValidation;
        const oldTypeSupportsRichText = oldType?.supportsRichText;
        // --- END EDIT 1 ---

        // --- NEW: Determine data range ---
        const startRow = 2;
        const endRow = this.currentSheet.rows.size + 1;
        const columnLetter = worksheet.getColumn(columnIndex).letter; // Get letter for clearing formats below
        // --- END NEW ---

        runInAction(() => {
            // Update column type in our model
            column.type = newType;

            // Update data validation in Excel of the header cell (identifies column type)
            this._setColumnTypeValidation(headerCell, newType);

            // --- EDIT 2: Logic to handle custom validation types vs others ---
            if (newType.hasCustomValidation) {
                // --- NEW: Collect unique existing values for initial options ---
                const uniqueValues = new Set();
                this.currentSheet.rows.forEach(rowData => {
                    const cellData = rowData[column.heading];
                    if (cellData && cellData.value !== null && cellData.value !== undefined) {
                        let valueToAdd = cellData.value;
                        // Extract text from rich text if necessary
                        if (Array.isArray(valueToAdd)) {
                            valueToAdd = valueToAdd.map(part => part.text).join('');
                        } else if (typeof valueToAdd === 'object' && valueToAdd.richText) {
                            valueToAdd = valueToAdd.richText.map(part => part.text).join('');
                        }
                        // Convert non-strings to string and trim
                        valueToAdd = String(valueToAdd).trim();
                        // Add non-empty values
                        if (valueToAdd) {
                            uniqueValues.add(valueToAdd);
                        }
                    }
                });
                const initialOptions = Array.from(uniqueValues);
                // --- END NEW ---

                // Set validation options in our local model first
                column.setValidationOptions(initialOptions);

                // Then apply the validation to Excel data rows
                this.applyDefaultColumnValidation(columnId, initialOptions);

                // --- NEW: Clear existing non-CF formatting, then READ conditional formatting ---
                if (startRow <= endRow) {
                    for (let rowNumber = startRow; rowNumber <= endRow; rowNumber++) {
                        const cell = worksheet.getCell(`${columnLetter}${rowNumber}`);
                        // Clear rich text if old type supported it
                        if (oldTypeSupportsRichText && cell.value && typeof cell.value === 'object' && cell.value.richText) {
                            cell.value = cell.value.richText.map(part => part.text).join('');
                        }
                        // Reset basic font/alignment
                        cell.font = { bold: false, italic: false, underline: false };
                        cell.alignment = { wrapText: false, vertical: 'center' };
                    }
                    // Now read existing CF rules that might match the new options
                    ConditionalFormattingHelper.readConditionalFormatting(worksheet, column, columnIndex);
                }
                // --- END NEW ---

                // Convert existing cell values if needed (handles rich text -> plain text)
                if (oldTypeSupportsRichText) {
                    this.currentSheet.rows.forEach(rowData => {
                        if (rowData[column.heading]) {
                            const value = rowData[column.heading].value;
                            if (Array.isArray(value)) {
                                rowData[column.heading].value = value.map(part => part.text).join('');
                            } else if (value && typeof value === 'object' && value.richText) {
                                rowData[column.heading].value = value.richText.map(part => part.text).join('');
                            }
                            // Ensure the value is one of the options, otherwise clear it
                            if (rowData[column.heading].value !== null && !initialOptions.includes(String(rowData[column.heading].value))) {
                                console.log(`[UpdateColumnType] Clearing value "${rowData[column.heading].value}" as it's not in new options for ${column.heading}`);
                                rowData[column.heading].value = null;
                            }
                        }
                    });
                    // Also update Excel cells (might be redundant if saveChanges runs, but good for immediate consistency)
                    for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
                        const cell = worksheet.getCell(`${columnLetter}${rowNumber}`);
                        if (cell.value && typeof cell.value === 'object' && cell.value.richText) {
                            const plainText = cell.value.richText.map(part => part.text).join('');
                            cell.value = initialOptions.includes(plainText) ? plainText : null;
                        } else if (cell.value !== null && !initialOptions.includes(String(cell.value))) {
                             cell.value = null;
                        }
                        // Reset cell formatting (bold/italic/underline)
                        cell.font = { bold: false, italic: false, underline: false };
                        cell.alignment = { wrapText: false, vertical: 'center' }; // Reset alignment
                    }
                }
                // --- END NEW ---

            } else {
                // --- EXISTING LOGIC for non-custom validation types ---
                // Clear validation options in our model
                column.setValidationOptions([]);


                // --- NEW: Clear Conditional Formatting if old type supported it ---
                if (oldTypeSupportsCustomValidation && startRow <= endRow) {
                     ConditionalFormattingHelper.clearConditionalFormatting(worksheet, columnIndex, startRow, endRow);
                }
                // --- END NEW ---

                // Clear all list validations and formatting in this column
                if (startRow <= endRow) {
                    for (let rowNumber = startRow; rowNumber <= endRow; rowNumber++) {
                        const cell = worksheet.getCell(`${columnLetter}${rowNumber}`);
                        // Clear list validation
                        if (cell.dataValidation?.type === 'list') {
                             cell.dataValidation = undefined;
                        }

                        // Clear rich text formatting if the old type supported it
                        if (oldTypeSupportsRichText && cell.value && typeof cell.value === 'object' && cell.value.richText) {
                            cell.value = cell.value.richText.map(part => part.text).join('');
                        }

                        // Reset cell formatting
                        cell.font = { bold: false, italic: false, underline: false };
                        cell.alignment = { wrapText: false, vertical: 'center' };
                    }
                }
                // --- END EXISTING LOGIC adaptation ---

                // --- NEW: Clear rich text in local model if old type supported it ---
                if (oldTypeSupportsRichText) {
                    this.currentSheet.rows.forEach(rowData => {
                        if (rowData[column.heading]) {
                            const value = rowData[column.heading].value;
                            if (Array.isArray(value)) {
                                rowData[column.heading].value = value.map(part => part.text).join('');
                            } else if (value && typeof value === 'object' && value.richText) {
                                rowData[column.heading].value = value.richText.map(part => part.text).join('');
                            }
                        }
                    });
                }
                // --- END NEW ---
            }
            // --- END EDIT 2 ---

            // Update all cell types in this column (always do this)
            this.currentSheet.rows.forEach(rowData => {
                if (rowData[column.heading]) {
                    rowData[column.heading].type = newType;
                    // Value conversion/clearing is handled within the if/else blocks above
                } else {
                    // Ensure cell data exists even if value was null previously
                    rowData[column.heading] = { value: null, type: newType, properties: {} };
                }
            });
        });

        // this.rootStore.fileSystemStore.markUnsaved(); // Already called by markSheetAsModified
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    // New helper method for applying validations directly
    async applyDefaultColumnValidation(columnId, options) {
        const column = this.currentSheet.columns.find(col => col.id === columnId);
        if (!column) return;

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) return;

        const columnIndex = this.currentSheet.columns.indexOf(column) + 1;
        const columnLetter = worksheet.getColumn(columnIndex).letter;

        // Apply validation to all rows except header
        for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
            const cell = worksheet.getCell(`${columnLetter}${rowNumber}`);
            cell.dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [`"${options.join(',')}"`]
            };
        }
    }

    async moveColumnToPosition(columnId, targetIndex) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        const currentIndex = this.currentSheet.columns.findIndex(col => col.id === columnId);
        if (currentIndex === -1) return;

        // No-op if same position
        if (currentIndex === targetIndex) return;

        // --- NEW: Get column info and range BEFORE moving ---
        const column = this.currentSheet.columns[currentIndex];
        const columnSupportsCf = column.type.hasCustomValidation;
        const startRow = 2;
        const endRow = this.currentSheet.rows.size + 1;
        // --- END NEW ---


        runInAction(() => {
            // --- NEW: Clear CF from the *original* position --- 
            // Check if column supports CF before clearing
            if (columnSupportsCf && startRow <= endRow) {
                 console.log(`[MoveColumn CF] Clearing CF from original index ${currentIndex + 1}`);
                 ConditionalFormattingHelper.clearConditionalFormatting(
                     worksheet,
                     currentIndex + 1, // Original Excel index (1-based)
                     startRow,
                     endRow
                 );
            }
            // --- END NEW ---

            // Handle Excel worksheet updates first (moves underlying cells/formats)
            CellValidationHelper.handleColumnMoveComplete(
                worksheet,
                currentIndex + 1, // Excel is 1-based
                targetIndex + 1,
                this.currentSheet.columns.length
            );

            // Then update local model
            const [movedColumn] = this.currentSheet.columns.splice(currentIndex, 1);
            this.currentSheet.columns.splice(targetIndex, 0, movedColumn);

            // Update row data order
            this.currentSheet.rows.forEach((rowData, rowId) => {
                // --- EDIT START: Preserve original data and rebuild using IDs ---
                const originalRowData = { ...rowData }; // Shallow copy is enough here
                const updatedRowData = {};

                // Always preserve outline level
                updatedRowData._outlineLevel = originalRowData._outlineLevel || 0;

                // Iterate based on the *new* column order
                this.currentSheet.columns.forEach(column => {
                    // Find the original data for this column using its ID
                    const originalCellData = originalRowData[column.id];

                    if (originalCellData !== undefined) {
                        // Copy existing data if it exists for this column ID
                        updatedRowData[column.id] = { ...originalCellData }; // Copy the cell data object
                    } else {
                        // If data for this column ID didn't exist (e.g., newly added column during move?), initialize
                        // This case might be less likely in a pure move, but good for robustness
                        updatedRowData[column.id] = { value: null, type: column.type, properties: {} };
                    }
                });
                // --- EDIT END ---
                this.currentSheet.rows.set(rowId, updatedRowData);
            });
        });

        // --- NEW: Re-apply CF for all columns to ensure correct ranges/priorities ---
        // Excel's move might shift formatting, but re-applying ensures our model is the source of truth.
        await this._reapplyConditionalFormattingForAllColumns(worksheet);
        // --- END NEW ---

        this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified

        // --- NEW: Explicitly re-apply CF for moved column IF it supports it --- 
        const movedColumnModel = this.currentSheet.columns[targetIndex]; // Get model at new index
        if (movedColumnModel && movedColumnModel.type.hasConditionalFormatting && startRow <= endRow) {
            console.log(`[MoveColumn CF] Explicitly reapplying CF for moved column "${movedColumnModel.heading}" at new index ${targetIndex + 1}`);
            this._reapplyColumnConditionalFormatting(worksheet, movedColumnModel, targetIndex + 1);
        }
        // --- END NEW ---
    }

    async addColumnRelative(columnId, position = 'right') {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const currentIndex = this.currentSheet.columns.findIndex(col => col.id === columnId);
        if (currentIndex === -1) return;

        const insertIndex = position === 'left' ? currentIndex : currentIndex + 1;
        const newHeading = `Column ${this.currentSheet.columns.length + 1}`;
        
        const column = await this.addColumn(newHeading, ColumnTypes.SINGLE_LINE_TEXT, insertIndex);
        return column;
    }

    async updateColumnWidth(columnId, excelUnits) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const column = this.currentSheet.columns.find(col => col.id === columnId);
        if (!column) return;

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        runInAction(() => {
            // Update width in our model (store as Excel units)
            column.setWidth(excelUnits);

            // Update Excel worksheet (already in Excel units)
            const columnIndex = this.currentSheet.columns.indexOf(column) + 1;
            const excelColumn = worksheet.getColumn(columnIndex);
            excelColumn.width = excelUnits;
        });

        // await this.rootStore.sheetStore._forceWorkbookUpdate();
        this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    applyColumnFormats(worksheet, columns) {
        if (!worksheet || !columns) return;
        
        columns.forEach((column, index) => {
            const col = worksheet.getColumn(index + 1);
            
            switch (column.type?.id) {
                case 'PERCENTAGE':
                    col.numFmt = '0.00%';
                    break;
                case 'NUMBER':
                    col.numFmt = '#,##0.00';
                    break;
                case 'RATING':
                    col.numFmt = '0';
                    break;
                case 'MULTI_LINE_TEXT':
                case 'SINGLE_LINE_TEXT':
                    col.numFmt = '@';
                    break;
                default:
                    col.numFmt = 'General';
            }
        });
    }

    _getColumnTypeFromCell(cell) {
        try {
            const dataValidation = cell.dataValidation;
            // --- REMOVED Logging ---

            if (!dataValidation || !dataValidation.formulae?.[0]) {
                // --- MODIFICATION START ---
                // No validation exists - determine default type, DO NOT SET IT HERE.
                // The actual setting will happen on save or type change if needed.
                // --- REMOVED Logging ---
                return ColumnTypes.SINGLE_LINE_TEXT;
                // --- MODIFICATION END ---
            }

            // Find matching type from our ColumnTypes
            // Remove quotes from the validation value if they exist
            const validationType = dataValidation.formulae[0].replace(/['"]/g, '');
            const matchedType = Object.values(ColumnTypes).find(type => type.label === validationType);

            // --- REMOVED Logging ---

            if (!matchedType) {
                console.warn('Unknown validation type:', validationType);
                // --- MODIFICATION START ---
                // Unknown type - determine default type, DO NOT SET IT HERE.
                // --- REMOVED Logging ---
                return ColumnTypes.SINGLE_LINE_TEXT;
                // --- MODIFICATION END ---
            }

            return matchedType;
        } catch (error) {
            console.warn('Error getting column type from cell:', error);
            // --- MODIFICATION START ---
            // Error - determine default type, DO NOT SET IT HERE.
            // --- REMOVED Logging ---
            return ColumnTypes.SINGLE_LINE_TEXT;
            // --- MODIFICATION END ---
        }
    }

    _setColumnTypeValidation(cell, columnType) {
        try {
            // Create a proper Excel formula for data validation
            // Using quoted string for the validation value
            cell.dataValidation = CellValidationHelper.createColumnTypeValidation(columnType);

            console.log('Set column validation:', {
                cellAddress: cell.address,
                type: columnType.id,
                validationValue: columnType.label,
                formula: `"${columnType.label}"`
            });
        } catch (error) {
            console.error('Error setting column validation:', error);
        }
    }

    // Helper method to get Excel validation for a column type
    _getValidationForType(type) {
        switch (type.id) {
            case ColumnTypes.DATE.id:
                return {
                    type: 'date',
                    operator: 'between',
                    showErrorMessage: true,
                    errorStyle: 'error',
                    errorTitle: 'Invalid Date',
                    error: 'Please enter a valid date'
                };
            case ColumnTypes.NUMBER.id:
                return {
                    type: 'decimal',
                    operator: 'between',
                    showErrorMessage: true,
                    errorStyle: 'error',
                    errorTitle: 'Invalid Number',
                    error: 'Please enter a valid number'
                };
            default:
                return null; // No validation for text columns
        }
    }

    /***************************************
     *               ROWS                  *
     ***************************************/

    /**
     * Row operations include:
     * - Adding rows
     * - Deleting rows
     * - Moving rows
     * - Updating row data
     */
    
    _addRow(sheet, rowData) {
        const rowId = uuidv4();
        sheet.rows.set(rowId, rowData);
        return rowId;
    }

    async addRow(initialData = {}, options = { relativeTo: null, position: 'below' }) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        // Create row data structure with all columns
        const completeRowData = {};
        this.currentSheet.columns.forEach(column => {
            completeRowData[column.heading] = {
                value: initialData[column.id] ?? null,
                type: column.type
            };
        });

        // Generate the row ID outside runInAction
        const newRowId = `row_${uuidv4()}`; // Generate a unique ID
        const newRowData = {};

        // Initialize cells for all columns using column.id
        this.currentSheet.columns.forEach(column => {
            // Explicitly check if the initialData contains a value for this specific column ID
            const initialValue = initialData.hasOwnProperty(column.id) 
                ? initialData[column.id] 
                // --- MODIFICATION START: Default Checkbox to False --- 
                : (column.type.id === ColumnTypes.CHECKBOX.id ? false : column.defaultValue); 
                // --- MODIFICATION END ---
                
            newRowData[column.id] = {
                value: initialValue,
                type: column.type,
                properties: {}
            };
        });
        
        // Get the outline level from the reference row or default to 0
        let baseOutlineLevel = 0;
        if (options.relativeTo && this.currentSheet.rows.has(options.relativeTo)) {
            const referenceRowData = this.currentSheet.rows.get(options.relativeTo);
            if (referenceRowData) {
                baseOutlineLevel = referenceRowData._outlineLevel || 0;
            }
        }
        newRowData._outlineLevel = baseOutlineLevel; // Add outline level to the new row data

        let excelInsertIndex = -1; // Initialize outside runInAction to be available later

        runInAction(() => {
            const rowsMap = this.currentSheet.rows;
            const allRowIds = Array.from(rowsMap.keys());
            let insertIndex = -1; // 0-based index for the Map/array operations

            if (options.relativeTo) {
                const relativeIndex = allRowIds.indexOf(options.relativeTo);
                if (relativeIndex !== -1) {
                     insertIndex = options.position === 'above' ? relativeIndex : relativeIndex + 1;
                } else {
                     console.warn(`[AddRow] Relative row ID "${options.relativeTo}" not found. Adding to bottom.`);
                     insertIndex = allRowIds.length; // Fallback to bottom
                }
            } else {
                 insertIndex = allRowIds.length; // Default to bottom
            }
            
            // Calculate excelInsertIndex here, inside runInAction, using the determined insertIndex
            excelInsertIndex = insertIndex + 2; // +2 for header row

            // Insert into the MobX map (this will trigger reactions)
            if (insertIndex >= allRowIds.length) {
                 // Add to end
                 rowsMap.set(newRowId, newRowData);
            } else {
                 // Insert at specific position
                 const entries = Array.from(rowsMap.entries());
                 entries.splice(insertIndex, 0, [newRowId, newRowData]);
                 // Recreate the map to ensure order and trigger MobX update correctly
                 this.currentSheet.rows = new Map(entries);
            }
            
            this.currentSheet.rowCount = this.currentSheet.rows.size; // Update row count
        });
        
        // --- Excel Worksheet Update ---
        // Now uses excelInsertIndex calculated within runInAction
        if (excelInsertIndex !== -1) { // Check if index was successfully calculated
            try {
                worksheet.spliceRows(excelInsertIndex, 0, []); // Insert empty row(s) - needs correct number if batching
                const newExcelRow = worksheet.getRow(excelInsertIndex);
                newExcelRow.outlineLevel = newRowData._outlineLevel;
                
                // Set values and validation for the new Excel row
                this.currentSheet.columns.forEach((column, colIndex) => {
                    const cell = newExcelRow.getCell(colIndex + 1);
                    cell.value = newRowData[column.id]?.value ?? null;
                    // Apply validation (might need helper)
                    if (column.type.hasCustomValidation) {
                         cell.dataValidation = CellValidationHelper.createOptionsValidation(column.validationOptions);
                    } else if (column.type.id === ColumnTypes.CHECKBOX.id) {
                         // Ensure checkbox validation is applied to the new Excel row
                         cell.dataValidation = {
                             type: 'list', allowBlank: true, formulae: ['"TRUE,FALSE"']
                         };
                    } else {
                        cell.dataValidation = undefined; // Clear validation
                    }
                });
                newExcelRow.commit();
            } catch (excelError) {
                 console.error("[AddRow] Error updating Excel worksheet directly:", excelError);
                 // Decide how to handle partial failure - maybe revert model change?
            }
        } else {
             console.error("[AddRow] Failed to determine valid insertion index for Excel update.");
        }
        // --- End Excel Worksheet Update ---

        this.markSheetAsModified(this.currentSheet?.name);
        return newRowId; // Return the ID of the newly added row
    }

    // Implement deleteRows method that can handle both single and multiple row deletions
    async deleteRows(rowIds) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }
        if (!rowIds || rowIds.length === 0) {
            console.warn('[DeleteRows] No row IDs provided for deletion.');
            return; // Nothing to delete
        }

        console.log('[DeleteRows] Deleting rows:', rowIds);

        try {
            const worksheet = this.service.getWorksheet(this.currentSheet.name);
            if (!worksheet) {
                throw new Error('Worksheet not found');
            }

            // --- EDIT START ---
            // Get the current order of all row IDs from the model
            const initialAllRowIds = Array.from(this.currentSheet.rows.keys());
            const excelRowsToDelete = [];

            // 1. Identify Excel row numbers and delete from the model
            runInAction(() => {
                for (const rowId of rowIds) {
                    // Find the index of the row in the *current* model state
                    const index = initialAllRowIds.indexOf(rowId);

                    if (index !== -1) {
                        // Calculate the corresponding Excel row number (index + header row + 1-based)
                        const excelRowNumber = index + 2;
                        excelRowsToDelete.push(excelRowNumber);

                        // Delete the row from the MobX model
                        this.currentSheet.rows.delete(rowId);
                        console.log(`[DeleteRows] Marked row ${rowId} (Excel row ${excelRowNumber}) for deletion.`);
                    } else {
                        console.warn(`[DeleteRows] Row ID ${rowId} not found in current sheet data.`);
                    }
                }
            });

            // 2. Sort the Excel row numbers in descending order to avoid index issues during splicing
            excelRowsToDelete.sort((a, b) => b - a);

            // 3. Splice rows from the Excel worksheet
            if (excelRowsToDelete.length > 0) {
                console.log('[DeleteRows] Splicing Excel rows:', excelRowsToDelete);
                for (const excelRowNumber of excelRowsToDelete) {
                    // Ensure the row number is valid before splicing
                    if (excelRowNumber > 0 && excelRowNumber <= worksheet.rowCount) {
                         worksheet.spliceRows(excelRowNumber, 1);
                    } else {
                         console.warn(`[DeleteRows] Attempted to splice invalid Excel row number: ${excelRowNumber}`);
                    }
                }
            } else {
                 console.log('[DeleteRows] No valid Excel rows found to splice.');
            }
            // --- EDIT END ---

            // --- NEW: Re-apply Conditional Formatting AFTER rows are deleted ---
            // The row range has changed, so re-apply to all relevant columns.
            await this._reapplyConditionalFormattingForAllColumns(worksheet);
            // --- END NEW ---

            // Sync the outline levels to the worksheet AFTER deletions
            await this.syncOutlineLevelsToWorksheet();

            // Mark the file as unsaved
            this.rootStore.fileSystemStore.markUnsaved();
            this.markSheetAsModified(this.currentSheet?.name); // Mark modified

            console.log('[DeleteRows] Rows deleted successfully');
        } catch (error) {
            console.error('[DeleteRows] Error deleting rows:', error);
            // Re-throw the error so the calling function knows deletion failed
            throw error;
        }
    }

    // Remove the deleteRow method since we're consolidating to just deleteRows
    // If deleteRow exists elsewhere in your code, you can keep it as an alias:
    async deleteRow(rowId) {
        return this.deleteRows(rowId);
    }

    async updateRow(rowId, updates) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const rowData = this.currentSheet.rows.get(rowId);
        if (!rowData) {
            throw new Error('Row not found');
        }

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        // Validate updates
        for (const [columnId, value] of Object.entries(updates)) {
            const column = this.currentSheet.columns.find(col => col.id === columnId);
            if (column && !column.isValidValue(value)) {
                throw new Error(`Invalid value for column ${column.heading}`);
            }
        }

        const rowIndex = Array.from(this.currentSheet.rows.keys()).indexOf(rowId) + 2; // +2 for header row

        runInAction(() => {
            // Update local model
            Object.entries(updates).forEach(([columnId, value]) => {
                const column = this.currentSheet.columns.find(col => col.id === columnId);
                if (column) {
                    // Use column.heading instead of columnId for accessing rowData
                    rowData[column.heading] = {
                        value,
                        type: column.type
                    };
                }
            });

            // Update Excel worksheet
            const row = worksheet.getRow(rowIndex);
            this.currentSheet.columns.forEach((column, columnIndex) => {
                const cell = row.getCell(columnIndex + 1);
                // Use column.heading to access the value
                cell.value = rowData[column.heading]?.value ?? null;
            });
        });

        // this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    async duplicateRows(rowIds) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        // Convert single rowId to array for consistency
        const rowIdsArray = Array.isArray(rowIds) ? rowIds : [rowIds];
        if (rowIdsArray.length === 0) return;

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }
        
        // Store outline levels of ALL rows to ensure completeness
        const allRowIds = Array.from(this.currentSheet.rows.keys());
        const outlineLevels = new Map();
        
        allRowIds.forEach(id => {
            const rowData = this.currentSheet.rows.get(id);
            outlineLevels.set(id, rowData._outlineLevel || 0);
        });

        // Get new row IDs before main operations
        const newRowIds = Array(rowIdsArray.length).fill(0).map(() => uuidv4());

        // Batch operations for better performance
        runInAction(() => {
            // 1. Pre-calculate all new row data
            const newRows = rowIdsArray.map((rowId, index) => {
                const originalData = this.currentSheet.rows.get(rowId);
                if (!originalData) return null;

                // Create a deep copy of the row data, including outline level
                const rowCopy = {};
                
                // Deep copy all properties except special ones
                Object.entries(originalData).forEach(([heading, data]) => {
                    if (heading !== '_outlineLevel') {
                        rowCopy[heading] = {
                            value: data.value,
                            type: data.type
                        };
                    }
                });
                
                // Explicitly copy outline level
                rowCopy._outlineLevel = originalData._outlineLevel || 0;
                
                return {
                    id: newRowIds[index],
                    data: rowCopy
                };
            }).filter(Boolean);

            // 2. Calculate insertion point
            const selectedIndices = rowIdsArray.map(id => allRowIds.indexOf(id)).sort((a, b) => a - b);
            
            let insertAfterIndex;
            if (rowIdsArray.length === 1) {
                // Single row - insert below
                insertAfterIndex = selectedIndices[0];
            } else {
                // Check if rows are consecutive
                const isConsecutive = selectedIndices.every((val, idx, arr) => 
                    idx === 0 || val === arr[idx - 1] + 1
                );
                
                if (isConsecutive) {
                    // Multiple consecutive rows - insert below last selected
                    insertAfterIndex = selectedIndices[selectedIndices.length - 1];
                } else {
                    // Multiple non-consecutive rows - insert at bottom
                    insertAfterIndex = allRowIds.length - 1;
                }
            }

            // 3. Prepare Excel worksheet operations
            const startRow = insertAfterIndex + 3; // +2 for header row, +1 to insert after
            
            // Create empty rows in Excel
            worksheet.spliceRows(startRow, 0, ...Array(newRows.length).fill(null));

            // 4. Batch update Excel cells with values and outline levels
            newRows.forEach((row, index) => {
                const rowNumber = startRow + index;
                const excelRow = worksheet.getRow(rowNumber);
                
                // Set outline level in Excel
                excelRow.outlineLevel = row.data._outlineLevel;

                this.currentSheet.columns.forEach((column, colIndex) => {
                    const cell = excelRow.getCell(colIndex + 1);
                    cell.value = row.data[column.heading]?.value ?? null;
                    
                    // Copy validation from original row if exists
                    const originalRowNumber = selectedIndices[Math.min(index, selectedIndices.length - 1)] + 2;
                    const originalCell = worksheet.getCell(originalRowNumber, colIndex + 1);
                    if (originalCell?.dataValidation) {
                        cell.dataValidation = { ...originalCell.dataValidation };
                    }
                });
            });

            // 5. Insert new rows into our data model
            const updatedRows = new Map();
            let inserted = false;
            
            allRowIds.forEach((id, index) => {
                // Add to map
                updatedRows.set(id, this.currentSheet.rows.get(id));
                
                // If this is where we insert the new rows
                if (index === insertAfterIndex && !inserted) {
                    inserted = true;
                    // Add all new rows
                    newRows.forEach(row => {
                        updatedRows.set(row.id, row.data);
                    });
                }
            });
            
            // In case we're adding to the end
            if (!inserted) {
                newRows.forEach(row => {
                    updatedRows.set(row.id, row.data);
                });
            }
            
            // Update the model with our new map
            this.currentSheet.rows = updatedRows;
            
            // Calculate new IDs for shifted rows
            const updatedRowIds = new Map(); // Map of old ID to new ID for shifted rows
            allRowIds.slice(insertAfterIndex + 1).forEach(id => {
                if (id.startsWith('row-')) {
                    const oldNumber = parseInt(id.substring(4), 10);
                    const newNumber = oldNumber + newRows.length; // Increment by number of inserted rows
                    const newId = `row-${newNumber}`;
                    updatedRowIds.set(id, newId);
                }
            });
            
            // Update the row IDs and preserve outline levels
            updatedRowIds.forEach((newId, oldId) => {
                if (this.currentSheet.rows.has(oldId)) {
                    const rowData = { ...this.currentSheet.rows.get(oldId) };
                    
                    // Get outline level from our stored map
                    const level = outlineLevels.get(oldId);
                    if (level !== undefined) {
                        rowData._outlineLevel = level;
                    }
                    
                    // Add with new ID
                    this.currentSheet.rows.set(newId, rowData);
                    // Delete old ID
                    this.currentSheet.rows.delete(oldId);
                }
            });
            
            // Ensure all rows after insertion have proper Excel outline levels
            for (let i = 0; i < newRows.length; i++) {
                const rowNumber = startRow + i;
                const excelRow = worksheet.getRow(rowNumber);
                excelRow.outlineLevel = newRows[i].data._outlineLevel;
            }
            
            // Update all following rows
            const allRowIdsAfterInsert = Array.from(this.currentSheet.rows.keys());
            for (let i = insertAfterIndex + 1 + newRows.length; i < allRowIdsAfterInsert.length; i++) {
                const rowId = allRowIdsAfterInsert[i];
                const rowData = this.currentSheet.rows.get(rowId);
                const excelRowNumber = i + 2; // +2 for header row
                
                if (rowData) {
                    const excelRow = worksheet.getRow(excelRowNumber);
                    excelRow.outlineLevel = rowData._outlineLevel || 0;
                }
            }
        });
        
        // Perform a full outline level sync to ensure everything is correct
        await this.syncOutlineLevelsToWorksheet();
        
        // Force update in one batch
        // await this.rootStore.sheetStore._forceWorkbookUpdate();
        this.rootStore.fileSystemStore.markUnsaved();
        
        return newRowIds;
    }

    async moveRowToPosition(rowId, targetIndex) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        const rowIds = Array.from(this.currentSheet.rows.keys());
        const currentIndex = rowIds.indexOf(rowId);
        if (currentIndex === -1) return;

        // No-op if same position
        if (currentIndex === targetIndex) return;

        // Store outline levels of both rows before moving
        const sourceExcelRow = currentIndex + 2;
        const targetExcelRow = targetIndex + 2;
        const sourceRow = worksheet.getRow(sourceExcelRow);
        let sourceOutlineLevel = CellOutlineLevelHelper.getOutlineLevel(sourceRow);

        // Validate outline level for the new position
        sourceOutlineLevel = CellOutlineLevelHelper.validateMovingRowLevel(
            worksheet,
            targetExcelRow,
            sourceOutlineLevel
        );

        runInAction(() => {
            // Update local model
            const [movedId] = rowIds.splice(currentIndex, 1);
            rowIds.splice(targetIndex, 0, movedId);

            const newRows = new Map();
            rowIds.forEach(id => {
                newRows.set(id, this.currentSheet.rows.get(id));
            });
            this.currentSheet.rows = newRows;

            // Update Excel worksheet
            const sourceRowData = sourceRow.values;

            // If moving down
            if (currentIndex < targetIndex) {
                // Move all rows between source and target up one position
                for (let i = sourceExcelRow; i < targetExcelRow; i++) {
                    const nextRow = worksheet.getRow(i + 1);
                    worksheet.getRow(i).values = nextRow.values;
                    CellOutlineLevelHelper.copyOutlineLevel(nextRow, worksheet.getRow(i));
                }
                // Place source row at target position with validated outline level
                worksheet.getRow(targetExcelRow).values = sourceRowData;
                worksheet.getRow(targetExcelRow).outlineLevel = sourceOutlineLevel;
            }
            // If moving up
            else {
                // Move all rows between target and source down one position
                for (let i = sourceExcelRow; i > targetExcelRow; i--) {
                    const prevRow = worksheet.getRow(i - 1);
                    worksheet.getRow(i).values = prevRow.values;
                    CellOutlineLevelHelper.copyOutlineLevel(prevRow, worksheet.getRow(i));
                }
                // Place source row at target position with validated outline level
                worksheet.getRow(targetExcelRow).values = sourceRowData;
                worksheet.getRow(targetExcelRow).outlineLevel = sourceOutlineLevel;
            }
        });

        // --- NEW: Re-apply Conditional Formatting AFTER row is moved ---
        // The effective range might not change size, but content shift can affect rules.
        // Re-applying ensures correctness.
        await this._reapplyConditionalFormattingForAllColumns(worksheet);
        // --- END NEW ---

        // await this.rootStore.sheetStore._forceWorkbookUpdate();
        this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    async moveRowsToPosition(rowIds, targetIndex) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        const allRowIds = Array.from(this.currentSheet.rows.keys());
        const totalRows = allRowIds.length;

        // Validate target index
        if (targetIndex < 0 || targetIndex > totalRows) {
            throw new Error('Invalid target position');
        }

        // Sort rowIds based on their current position to maintain relative order
        const sortedRowIds = rowIds.sort((a, b) => {
            return allRowIds.indexOf(a) - allRowIds.indexOf(b);
        });

        // Store outline levels of moving rows
        const movingRowLevels = new Map();
        sortedRowIds.forEach(rowId => {
            const currentIndex = allRowIds.indexOf(rowId);
            const excelRow = worksheet.getRow(currentIndex + 2);
            movingRowLevels.set(rowId, CellOutlineLevelHelper.getOutlineLevel(excelRow));
        });

        // Calculate adjusted target index
        let adjustedTargetIndex = targetIndex;
        sortedRowIds.forEach(rowId => {
            const currentIndex = allRowIds.indexOf(rowId);
            if (currentIndex < targetIndex) {
                adjustedTargetIndex--;
            }
        });

        adjustedTargetIndex = Math.max(0, Math.min(adjustedTargetIndex, totalRows - sortedRowIds.length));

        // Add this logging before processing
        console.log('[OutlineLevel] Moving rows:', {
            rowIds,
            targetIndex,
            movingRowLevels: Array.from(movingRowLevels.entries())
        });

        runInAction(() => {
            // 1. Store the rows to be moved
            const movingRows = new Map();
            sortedRowIds.forEach(rowId => {
                movingRows.set(rowId, this.currentSheet.rows.get(rowId));
            });

            // 2. Create new row order
            const newRowIds = allRowIds.filter(id => !movingRows.has(id));
            newRowIds.splice(adjustedTargetIndex, 0, ...sortedRowIds);

            // 3. Create new rows Map with the updated order
            const newRows = new Map();
            newRowIds.forEach(rowId => {
                newRows.set(rowId, movingRows.has(rowId) 
                    ? movingRows.get(rowId) 
                    : this.currentSheet.rows.get(rowId)
                );
            });

            // 4. Update Excel worksheet
            // Store all existing rows' data and outline levels (except moving rows)
            const existingRows = new Map();
            allRowIds.forEach((rowId, index) => {
                if (!movingRows.has(rowId)) {
                    const row = worksheet.getRow(index + 2);
                    existingRows.set(rowId, {
                        values: row.values,
                        outlineLevel: CellOutlineLevelHelper.getOutlineLevel(row)
                    });
                }
            });

            // Clear all rows except header
            for (let i = worksheet.rowCount; i > 1; i--) {
                worksheet.spliceRows(i, 1);
            }

            // Add rows in new order
            newRowIds.forEach((rowId, index) => {
                const rowNumber = index + 2; // +2 for header row
                const rowData = newRows.get(rowId);
                const values = this.currentSheet.columns.map(column => 
                    rowData[column.heading]?.value ?? null
                );
                
                worksheet.insertRow(rowNumber, values);
                const row = worksheet.getRow(rowNumber);
                
                // Set outline level - use stored level for moving rows, or keep existing level
                let outlineLevel;
                if (movingRowLevels.has(rowId)) {
                    outlineLevel = movingRowLevels.get(rowId);
                    
                    // Special handling for top-level items (level 0)
                    if (outlineLevel === 0) {
                        // Level 0 items can be placed anywhere
                        row.outlineLevel = 0;
                    } else {
                        // For non-top-level items, validate the level
                        if (index === 0 || !movingRowLevels.has(newRowIds[index - 1])) {
                            outlineLevel = CellOutlineLevelHelper.validateMovingRowLevel(
                                worksheet,
                                rowNumber,
                                outlineLevel
                            );
                            
                            // Store the adjustment factor to apply to all children
                            const adjustmentFactor = outlineLevel - movingRowLevels.get(rowId);
                            
                            // Apply the same adjustment to all subsequent moved rows to maintain relative levels
                            if (adjustmentFactor !== 0) {
                                for (let i = index + 1; i < newRowIds.length; i++) {
                                    const nextRowId = newRowIds[i];
                                    if (movingRowLevels.has(nextRowId)) {
                                        movingRowLevels.set(
                                            nextRowId, 
                                            Math.max(0, movingRowLevels.get(nextRowId) + adjustmentFactor)
                                        );
                                    } else {
                                        break; // Stop at the first non-moved row
                                    }
                                }
                            }
                        } 
                        // For subsequent moved rows, ensure they maintain proper parent-child relationship
                        else {
                            const prevRowLevel = row.worksheet.getRow(rowNumber - 1).outlineLevel || 0;
                            outlineLevel = Math.min(outlineLevel, prevRowLevel + 1);
                        }
                        row.outlineLevel = outlineLevel;
                    }
                } else if (existingRows.has(rowId)) {
                    outlineLevel = existingRows.get(rowId).outlineLevel;
                    row.outlineLevel = outlineLevel || 0;
                } else {
                    row.outlineLevel = 0; // Default to level 0 for new rows
                }
            });

            // 5. Update the store
            this.currentSheet.rows = newRows;
        });

        // After all rows have been moved and updated
        // Ensure outline levels are correctly synced to the worksheet
        await this.syncOutlineLevelsToWorksheet();

        // --- NEW: Re-apply Conditional Formatting AFTER rows are moved ---
        await this._reapplyConditionalFormattingForAllColumns(worksheet);
        // --- END NEW ---

        // await this.rootStore.sheetStore._forceWorkbookUpdate();
        this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    async setRowOutlineLevel(rowId, level) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        // Get row index and check if it exists
        const rowIds = Array.from(this.currentSheet.rows.keys());
        const rowIndex = rowIds.indexOf(rowId);
        if (rowIndex === -1) return;

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        const excelRowIndex = rowIndex + 2; // +2 for header row
        const validatedLevel = CellOutlineLevelHelper.validateMovingRowLevel(
            worksheet,
            excelRowIndex,
            level
        );

        runInAction(() => {
            // Update Excel worksheet
            const row = worksheet.getRow(excelRowIndex);
            row.outlineLevel = validatedLevel;

            // Update local model
            const rowData = this.currentSheet.rows.get(rowId);
            rowData._outlineLevel = validatedLevel;
        });

        // this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    // Helper method to check if rows are consecutive
    _areRowsConsecutive(rowIds) {
        const allRowIds = Array.from(this.currentSheet.rows.keys());
        const indices = rowIds.map(id => allRowIds.indexOf(id)).sort((a, b) => a - b);
        
        // Check if each index is exactly one more than the previous
        return indices.every((val, idx, arr) => 
            idx === 0 || val === arr[idx - 1] + 1
        );
    }

    async batchSetRowOutlineLevels(rowIds, operation) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        // Validate rows are consecutive
        if (!this._areRowsConsecutive(rowIds)) {
            throw new Error('Can only perform batch indent/outdent on consecutive rows');
        }

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        const allRowIds = Array.from(this.currentSheet.rows.keys());
        
        // Sort rowIds by their position in the sheet
        const sortedRowIds = rowIds.sort((a, b) => 
            allRowIds.indexOf(a) - allRowIds.indexOf(b)
        );

        // Get the row above the first selected row for indent validation
        const firstRowIndex = allRowIds.indexOf(sortedRowIds[0]);
        const rowAboveId = firstRowIndex > 0 ? allRowIds[firstRowIndex - 1] : null;
        const rowAboveLevel = rowAboveId 
            ? (this.currentSheet.rows.get(rowAboveId)._outlineLevel || 0)
            : -1;

        runInAction(() => {
            let previousLevel = rowAboveLevel;

            for (const rowId of sortedRowIds) {
                const rowData = this.currentSheet.rows.get(rowId);
                const currentLevel = rowData._outlineLevel || 0;
                const rowIndex = allRowIds.indexOf(rowId);
                
                let newLevel;
                if (operation === 'indent') {
                    // Can only indent one level more than previous row
                    if (currentLevel <= previousLevel + 1) {
                        newLevel = Math.min(currentLevel + 1, previousLevel + 1);
                    } else {
                        continue; // Skip this row if it can't be indented
                    }
                } else {
                    // Outdent
                    newLevel = Math.max(0, currentLevel - 1);
                }

                // Apply the change if valid
                if (newLevel >= 0 && newLevel <= 8) {
                    const excelRowIndex = rowIndex + 2;
                    const row = worksheet.getRow(excelRowIndex);
                    row.outlineLevel = newLevel;
                    rowData._outlineLevel = newLevel;
                    previousLevel = newLevel; // Update previous level for next iteration
                }
            }
        });

        // await this.rootStore.sheetStore._forceWorkbookUpdate();
        this.rootStore.fileSystemStore.markUnsaved();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    async syncOutlineLevelsToWorksheet() {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        // RESTORE: Get the worksheet
        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        // Get all row IDs in the current sheet order
        // RESTORE: Get row IDs (variable name was already correct here)
        const allRowIds = Array.from(this.currentSheet.rows.keys());

        console.log('[OutlineSync] Starting outline level sync for', allRowIds.length, 'rows');

        // Ensure Excel worksheet has enough rows (optional, ExcelJS might handle this)
        // if (worksheet.rowCount < allRowIds.length + 1) {
        //     console.log(`[OutlineSync] Ensuring worksheet has at least ${allRowIds.length + 1} rows.`);
        //     // Add empty rows if needed. Be cautious if rows have specific heights/styles.
        //     // worksheet.addRows(new Array(allRowIds.length + 1 - worksheet.rowCount).fill(null));
        // }

        // Synchronize from model to Excel worksheet
        for (let i = 0; i < allRowIds.length; i++) {
            const rowId = allRowIds[i];
            const rowData = this.currentSheet.rows.get(rowId);
            const excelRowNumber = i + 2; // +2 for header row

            if (!rowData) {
                console.warn(`[OutlineSync] Missing row data for ID ${rowId} at index ${i}`);
                continue;
            }

            // Get the outline level, default to 0
            const level = typeof rowData._outlineLevel === 'number' ? rowData._outlineLevel : 0;

            try {
                // Get or create the Excel row
                const row = worksheet.getRow(excelRowNumber);

                // Set Excel row outline level directly
                // Check if the level actually needs changing to avoid unnecessary writes
                if (row.outlineLevel !== level) {
                     row.outlineLevel = level;
                     // Log only when a change is made
                     // console.log(`[OutlineSync] Set row ${excelRowNumber} (${rowId}) to level ${level}`);
                }

            } catch (error) {
                console.error(`[OutlineSync] Error setting outline level for row ${excelRowNumber} (ID: ${rowId}):`, error);
            }
        }

        // REMOVE _forceWorkbookUpdate call
        // await this.rootStore.sheetStore._forceWorkbookUpdate();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified

        console.log('[OutlineSync] Outline level sync completed');
    }

    /***************************************
     *               CELLS                 *
     ***************************************/

    /**
     * Cell operations include:
     * - Updating cell values
     * - Validating cell data
     * - Getting cell metadata
     * - Setting cell formatting
     */

    // USE THE USER-PROVIDED VERSION, ADAPTED FOR HEADING/ID and MODIFICATION TRACKING
    async updateCell(rowId, columnHeadingOrId, value, properties = {}) {
        if (!this.currentSheet) {
            console.warn('[UpdateCell] No sheet selected');
            throw new Error('No sheet selected');
        }

        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            console.warn('[UpdateCell] Worksheet not found:', this.currentSheet.name);
            throw new Error('Worksheet not found');
        }

        const rowData = this.currentSheet.rows.get(rowId);
        if (!rowData) {
            console.warn(`[UpdateCell] Row data not found for ID: ${rowId}`);
            return; // Exit if row data doesn't exist
        }

        // Find the column object by ID or Heading
        let column = null;
        if (typeof columnHeadingOrId === 'object' && columnHeadingOrId !== null && columnHeadingOrId.id) {
            // If it's already a column object
            column = columnHeadingOrId;
        } else if (typeof columnHeadingOrId === 'string') {
            // Try finding by ID first, then by heading
            column = this.currentSheet.columns.find(c => c.id === columnHeadingOrId) ||
                     this.currentSheet.columns.find(c => c.heading === columnHeadingOrId);
        }

        if (!column) {
            console.error('[UpdateCell] Column not found by ID or Heading:', columnHeadingOrId);
            throw new Error('Column not found in sheet');
        }

        // Get the exact column index (0-based)
        const columnIndex = this.currentSheet.columns.indexOf(column);
        if (columnIndex === -1) {
            // This shouldn't happen if the column was found above, but check anyway
            console.error('[UpdateCell] Column found but not in sheet columns array:', column);
            throw new Error('Column inconsistency');
        }

        // Get the exact row index (Excel 1-based, including header)
        // Find the index of the rowId within the keys array, then add 2 (1 for 0-based index, 1 for header)
        const excelRowIndex = Array.from(this.currentSheet.rows.keys()).indexOf(rowId) + 2;
        if (excelRowIndex < 2) { // Should be at least 2 (first data row)
            console.error('[UpdateCell] Invalid Excel row index calculated:', excelRowIndex, 'for rowId:', rowId);
            throw new Error('Invalid row index calculation');
        }

        console.log('[UpdateCell] Updating cell:', {
            rowId,
            excelRowIndex,
            columnIndex, // 0-based model index
            excelColIndex: columnIndex + 1, // 1-based Excel index
            columnHeading: column.heading,
            value
        });

        runInAction(() => {
            // --- Update local model ---
            if (!rowData[column.heading]) {
                 rowData[column.heading] = { value: null, type: column.type, properties: {} };
            }

            // --- PARSE/NORMALIZE VALUE BEFORE STORING IN MODEL ---
            let valueToStore = null; // Default to null
            if (value !== null && value !== undefined) {
                if (column?.type?.id === 'RICH_TEXT') {
                    let richTextArray = null;
                    if (Array.isArray(value)) {
                        // Already in our desired array format
                        richTextArray = value;
                    } else if (typeof value === 'string') {
                        // Try parsing stringified JSON
                        if (value.startsWith('[') && value.endsWith(']')) {
                            try {
                                const parsed = JSON.parse(value);
                                if (Array.isArray(parsed)) {
                                    richTextArray = parsed;
                                } else {
                                    richTextArray = [{ text: value }]; // Parsed but not array
                                }
                            } catch (e) {
                                richTextArray = [{ text: value }]; // Parse failed
                            }
                        } else {
                             richTextArray = [{ text: value }]; // Plain string
                        }
                    } else if (typeof value === 'object' && value.richText && Array.isArray(value.richText)) {
                         // Input is already ExcelJS format {richText: [...]} -> extract array
                         richTextArray = value.richText;
                    } else {
                        // Unexpected type, convert to string and wrap
                        console.warn(`[UpdateCell] Unexpected value type for Rich Text, wrapping: ${typeof value}`, value);
                        richTextArray = [{ text: String(value) }];
                    }

                    // Normalize: Store null if effectively empty
                    if (richTextArray && richTextArray.some(part => part.text && String(part.text).trim() !== '')) {
                        valueToStore = richTextArray;
                    } else {
                        valueToStore = null;
                    }
                } else {
                    // For non-rich text, store the value directly (or handle specific types like Date/URL if needed)
                    valueToStore = value;
                }
            }
            // --- END PARSING/NORMALIZATION ---

            // Get existing data for comparison (optional but good for debugging)
            const existingCellData = rowData[column.id];
            const oldValue = existingCellData?.value;

            console.log(`[UpdateCell] Updating Row ${rowId}, Col ${column.id} (${column.heading}) from`, oldValue, `to`, valueToStore);

            // --- Update local model by creating a new object --- 
            // This helps ensure MobX detects the change reliably
            rowData[column.id] = {
                ...existingCellData, // Spread existing properties like 'type', 'properties'
                value: valueToStore, // Set the new value
                type: column.type // Ensure type is updated/correct
            };
            // Merge properties if provided (do this after creating the new object)
            if (properties && Object.keys(properties).length > 0) {
                rowData[column.id].properties = { ...rowData[column.id].properties, ...properties };
            }
            // --- End local model update ---

            // --- Update Excel worksheet directly --- 
            // ... (Excel update logic remains the same) ...

            // --- Update Excel worksheet directly ---
            const row = worksheet.getRow(excelRowIndex);
            const cell = row.getCell(columnIndex + 1);

            // Convert model value to ExcelJS format
            let valueToSetExcel = null;
            
            // ADD LOGGING HERE TO CHECK COLUMN TYPE:
            // --- MODIFIED CHECK ---
            console.log(`[UpdateCell Type Check] Column: ${column?.heading}, Type ID: ${column?.type?.id}, Supports Rich Text?: ${column?.type?.supportsRichText}`);
            // --- END MODIFIED CHECK ---

            if (valueToStore !== null && valueToStore !== undefined) {
                // --- MODIFIED CHECK ---
                // Check if the column type supports rich text
                if (column?.type?.supportsRichText) {
                // --- END MODIFIED CHECK ---
                    // valueToStore should be the array [{...}] or null
                    if (Array.isArray(valueToStore)) {
                         valueToSetExcel = { richText: valueToStore }; // Wrap for ExcelJS
                    }
                    // If valueToStore is null, valueToSetExcel remains null
                } else if ((column?.type?.id === 'DATE' || column?.type?.id === 'START_DATE' || column?.type?.id === 'END_DATE') && valueToStore instanceof Date) {
                    valueToSetExcel = valueToStore; // Pass Date object directly
                } else if ((column?.type?.id === 'URL' || column?.type?.id === 'EMAIL') && typeof valueToStore === 'object' && valueToStore.text && valueToStore.hyperlink) {
                     valueToSetExcel = valueToStore; // Pass link object directly
                } else {
                    // Other primitive types
                    valueToSetExcel = valueToStore;
                }
            }

            // The original debug log (which wasn't firing)
            // --- MODIFIED CHECK ---
            if (column?.type?.supportsRichText && valueToSetExcel !== null) {
            // --- END MODIFIED CHECK ---
                console.log(`[UpdateCell Debug] Setting Rich Text for ${cell.address}:`, JSON.stringify(valueToSetExcel), typeof valueToSetExcel);
            }

            cell.value = valueToSetExcel; // Set Excel cell value
            // console.log('[UpdateCell] Set Excel cell value to:', valueToSetExcel);
    
            this.markSheetAsModified(this.currentSheet?.name);
        });
    }

    async updateColumnValidationOptions(columnId, options) {
        if (!this.currentSheet) {
            throw new Error('No sheet selected');
        }

        // RESTORE: Find the column object
        const column = this.currentSheet.columns.find(col => col.id === columnId);
        if (!column || !column.type.hasCustomValidation) {
            console.warn(`[UpdateValidation] Column ${columnId} not found or does not support custom validation.`);
            return;
        }

        // RESTORE: Get the worksheet
        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
            throw new Error('Worksheet not found');
        }

        // RESTORE: Get the column index
        const columnIndex = this.currentSheet.columns.indexOf(column);
        if (columnIndex === -1) {
             console.error(`[UpdateValidation] Column ${columnId} found but index is -1.`);
             return; // Should not happen if column was found
        }

        // --- NEW: Determine data range ---
        const startRow = 2;
        const endRow = this.currentSheet.rows.size + 1;
        // --- END NEW ---

        runInAction(() => {
            // Update column validation options in our model
            column.setValidationOptions(options);

            // --- NEW: Re-apply Conditional Formatting to reflect changes ---
            // This will clear old rules for this range and apply new ones based on current options/styles
            if (startRow <= endRow) {
                 ConditionalFormattingHelper.applyConditionalFormatting(
                     worksheet,
                     column,
                     columnIndex + 1, // Excel is 1-based
                     startRow,
                     endRow
                 );
            }
            // --- END NEW ---

            // --- MODIFICATION START: Defer cell value clearing until after Excel validation is applied ---
            // We don't modify the model's cell values directly here anymore.
            // Instead, rely on the Excel validation update below and potentially
            // a subsequent read/sync operation if strict model<->Excel value consistency is needed immediately.
            // This prevents clearing data based on a temporary mismatch during the update.
            /*
            this.currentSheet.rows.forEach((rowData, rowId) => {
                // Access using column.id
                const cellData = rowData[column.id];
                // Check if cellData exists and its value is now invalid according to the new options
                if (cellData && cellData.value !== null && cellData.value !== undefined && !column.isValidValue(cellData.value)) {
                    console.log(`[UpdateValidation] Clearing invalid value "${cellData.value}" in row ${rowId}, column ${column.heading}`);
                    // Reset invalid values in model using column.id
                    cellData.value = null;
                    // Ensure the cellData object reference changes if needed for reactivity
                    // rowData[column.id] = { ...cellData, value: null };

                    // Update Excel cell directly (remains the same)
                    // ...
                }
            });
            */
            // --- MODIFICATION END ---
        });

        // Update Excel worksheet validation (List validation)
        const columnLetter = worksheet.getColumn(columnIndex + 1).letter;
        const validationFormula = `"${options.join(',')}"`;

        // Skip header row (row 1) when applying validation
        // Iterate based on the number of rows in our data model + 1 (for header)
        const numDataRows = this.currentSheet.rows.size;
        for (let i = 0; i < numDataRows; i++) {
             const excelRowNumber = i + 2; // Data starts at row 2
             const cell = worksheet.getCell(`${columnLetter}${excelRowNumber}`);
             cell.dataValidation = {
                 type: 'list',
                 allowBlank: true,
                 formulae: [validationFormula]
             };
        }
        // Clear validation for rows beyond our data model in Excel
        for (let rowNumber = numDataRows + 2; rowNumber <= worksheet.rowCount; rowNumber++) {
             const cell = worksheet.getCell(`${columnLetter}${rowNumber}`);
             if (cell.dataValidation) {
                 cell.dataValidation = undefined;
             }
        }


        // REMOVE _forceWorkbookUpdate call
        // await this.rootStore.sheetStore._forceWorkbookUpdate();
        this.markSheetAsModified(this.currentSheet?.name); // Mark modified
    }

    async validateOutlineLevels(options = { applyFixes: false }) {
        if (!this.currentSheet) return 0;

        // RESTORE: Get the worksheet
        const worksheet = this.service.getWorksheet(this.currentSheet.name);
        if (!worksheet) return 0;

        console.log('[ValidateOutline] Validating outline levels for all rows...');
        // RESTORE: Initialize counter and get row IDs
        let inconsistencies = 0;
        const rowIds = Array.from(this.currentSheet.rows.keys());
        let previousLevel = 0; // Track the level of the previous row processed

        // Process rows in order
        for (let i = 0; i < rowIds.length; i++) {
            const rowId = rowIds[i];
            const rowData = this.currentSheet.rows.get(rowId);
            // RESTORE: Get current level
            const currentLevel = rowData._outlineLevel || 0;

            // A row can only be one level deeper than the previous row
            // RESTORE: Calculate valid level
            const validLevel = Math.min(currentLevel, previousLevel + 1);

            if (validLevel !== currentLevel) {
                console.log(`[ValidateOutline] Found outline level inconsistency for row ${i+1} (ID: ${rowId}): Level ${currentLevel} (should be <= ${previousLevel + 1}, corrected to ${validLevel})`);
                // RESTORE: Increment counter
                inconsistencies++;

                // Only fix if explicitly requested
                if (options.applyFixes) {
                    console.log(`[ValidateOutline] Fixing outline level for row ${i+1}: ${currentLevel} -> ${validLevel}`);

                    // Update Excel worksheet
                    const excelRowIndex = i + 2; // +2 for header row
                    const row = worksheet.getRow(excelRowIndex);
                    row.outlineLevel = validLevel;

                    // Update local model
                    runInAction(() => {
                        rowData._outlineLevel = validLevel;
                    });
                }
            }

            // Use the original level for the next comparison if not fixing,
            // or the corrected level if fixing.
            previousLevel = options.applyFixes ? validLevel : currentLevel;
        }

        if (inconsistencies > 0) {
            console.log(`[ValidateOutline] Found ${inconsistencies} outline level inconsistencies`);
            if (options.applyFixes) {
                console.log(`[ValidateOutline] Fixed ${inconsistencies} outline level inconsistencies`);
                // REMOVE _forceWorkbookUpdate call
                // await this.rootStore.sheetStore._forceWorkbookUpdate();
                this.markSheetAsModified(this.currentSheet?.name); // Mark modified only if fixes applied
            }
        } else {
            console.log('[ValidateOutline] All outline levels are consistent');
        }

        return inconsistencies;
    }

    // --- Helper Functions for Width Calculation ---

    /**
     * Estimates the width required for a given text string in Excel units.
     * NOTE: This is an approximation as we cannot directly measure text width.
     * @param {string} text The text content.
     * @param {boolean} isBold Whether the text is bold.
     * @returns {number} Estimated width in Excel units.
     */
    static _calculateExcelWidth(text, isBold = false) {
        const textStr = String(text ?? ''); // Ensure it's a string, handle null/undefined
        if (!textStr) return 0; // No width for empty string

        const factor = isBold ? CHAR_WIDTH_FACTOR_BOLD : CHAR_WIDTH_FACTOR_NORMAL;
        // Simple approximation: (character count * factor) + padding
        const estimatedWidth = (textStr.length * factor) + COL_WIDTH_PADDING;
        return estimatedWidth;
    }

    /**
     * Gets a display-friendly string representation of cell data for width calculation.
     * @param {object} cellData The cell data object (e.g., { value: ..., type: ... }).
     * @param {object} columnType The column type object.
     * @returns {string} A string representation of the value.
     */
    _getCellDisplayValue(cellData, columnType) {
        if (!cellData || cellData.value === null || cellData.value === undefined) {
            return '';
        }
        const value = cellData.value;

        if (columnType?.supportsRichText && Array.isArray(value)) {
            return value.map(part => part.text || '').join('');
        } else if (columnType?.supportsRichText && typeof value === 'object' && value.richText) {
             return value.richText.map(part => part.text || '').join('');
        } else if ((columnType?.id === 'URL' || columnType?.id === 'EMAIL') && typeof value === 'object' && value.text) {
            return String(value.text); // Use the display text for links
        } else if (value instanceof Date) {
            // Basic date formatting, adjust as needed
            return value.toLocaleDateString();
        } else {
            return String(value);
        }
    }

    // --- End Helper Functions ---

    // --- NEW: Helper method to re-apply CF for all columns ---
    async _reapplyConditionalFormattingForAllColumns(worksheet) {
        if (!this.currentSheet || !worksheet) return;
        console.log(`[CF Reapply All] Reapplying CF for all columns.`);
        this.currentSheet.columns.forEach((column, colIndex) => {
             this._reapplyColumnConditionalFormatting(worksheet, column, colIndex + 1); // Use the new helper
        });
        console.log(`[CF Reapply All] Finished reapplying CF.`);
    }
    // --- END NEW ---

    // --- Column Style Management ---

    /**
     * Sets the style for a specific option within a column in the current sheet.
     * Updates the column model and triggers re-application of conditional formatting.
     * @param {string} columnId - The ID of the column.
     * @param {string} optionValue - The option value (e.g., "Done").
     * @param {object} styleObject - The ExcelJS style object.
     */
    async setColumnOptionStyle(columnId, optionValue, styleObject) {
        if (!this.currentSheet) {
            console.warn('[SetOptionStyle] No current sheet.');
            return;
        }

        const column = this.currentSheet.columns.find(col => col.id === columnId);
        if (!column) {
            console.warn(`[SetOptionStyle] Column not found: ${columnId}`);
            return;
        }

        if (!column.type.hasCustomValidation) {
             console.warn(`[SetOptionStyle] Column "${column.heading}" does not support option styles.`);
             return;
        }

        const worksheet = this.service?.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
             console.warn(`[SetOptionStyle] Worksheet not found: ${this.currentSheet.name}`);
             return; // Cannot apply formatting without worksheet
        }

        runInAction(() => {
            // --- ADD LOGGING: Check the styleObject received ---
            console.log(`[SetOptionStyle DBG] Received styleObject for "${optionValue}":`, JSON.stringify(styleObject));
            // --- END LOGGING ---

            column.setOptionStyle(optionValue, styleObject);

            // --- Log the style *after* setting it in the observable map ---
            const storedStyle = column.optionStyles.get(optionValue);
            console.log(`[SetOptionStyle DBG] Style stored in column.optionStyles for "${optionValue}":`, storedStyle ? JSON.stringify(toJS(storedStyle)) : 'undefined');
            // --- End Logging ---

            console.log(`[SetOptionStyle] Updated style for option "${optionValue}" in column "${column.heading}"`, toJS(styleObject));
            this.markSheetAsModified(this.currentSheet.name);
        });

        // Re-apply conditional formatting for this specific column
        const columnIndex = this.currentSheet.columns.indexOf(column);
        await this._reapplyColumnConditionalFormatting(worksheet, column, columnIndex + 1); // Excel index is 1-based
    }

    /**
     * Removes the style associated with a specific option within a column.
     * Updates the column model and triggers re-application of conditional formatting.
     * @param {string} columnId - The ID of the column.
     * @param {string} optionValue - The option value whose style should be removed.
     */
    async removeColumnOptionStyle(columnId, optionValue) {
        if (!this.currentSheet) {
            console.warn('[RemoveOptionStyle] No current sheet.');
            return;
        }

        const column = this.currentSheet.columns.find(col => col.id === columnId);
        if (!column) {
            console.warn(`[RemoveOptionStyle] Column not found: ${columnId}`);
            return;
        }

         if (!column.type.hasCustomValidation) {
              console.warn(`[RemoveOptionStyle] Column "${column.heading}" does not support option styles.`);
              return;
         }

        const worksheet = this.service?.getWorksheet(this.currentSheet.name);
        if (!worksheet) {
             console.warn(`[RemoveOptionStyle] Worksheet not found: ${this.currentSheet.name}`);
             return; // Cannot apply formatting without worksheet
        }

        let styleExisted = false;
        runInAction(() => {
            styleExisted = column.optionStyles.has(optionValue);
            if (styleExisted) {
                column.removeOptionStyle(optionValue); // Use the Column model's action
                console.log(`[RemoveOptionStyle] Removed style for option "${optionValue}" in column "${column.heading}"`);
                this.markSheetAsModified(this.currentSheet.name);
            } else {
                console.log(`[RemoveOptionStyle] No style found for option "${optionValue}" in column "${column.heading}", nothing removed.`);
            }
        });

        // Only re-apply if a style was actually removed
        if (styleExisted) {
            const columnIndex = this.currentSheet.columns.indexOf(column);
            await this._reapplyColumnConditionalFormatting(worksheet, column, columnIndex + 1); // Excel index is 1-based
        }
    }

    // --- End Column Style Management ---

    // --- NEW: Helper for single column CF reapplication ---
    /**
     * Reapplies conditional formatting for a single column based on its current model state.
     * @param {ExcelJS.Worksheet} worksheet The worksheet instance.
     * @param {Column} column The column model instance.
     * @param {number} excelColIndex The 1-based Excel column index.
     */
    async _reapplyColumnConditionalFormatting(worksheet, column, excelColIndex) {
         if (!this.currentSheet || !worksheet || !column || excelColIndex < 1) return;

         const startRow = 2;
         const endRow = this.currentSheet.rows.size + 1; // Recalculate based on current rows

         if (startRow <= endRow) {
             // Apply only if the column type supports it
             if (column.type.hasCustomValidation) {
                 console.log(`[CF Reapply Single] Reapplying CF for column "${column.heading}" (${excelColIndex}), range ${startRow}-${endRow}`);
                 // Apply CF will clear existing rules for the range first, then add new ones
                 ConditionalFormattingHelper.applyConditionalFormatting(
                     worksheet,
                     column,
                     excelColIndex,
                     startRow,
                     endRow
                 );
             }
         } else {
             console.log(`[CF Reapply Single] Skipping CF reapplication for "${column.heading}" (no data rows).`);
             // Optional: If no rows, explicitly clear formatting for this column
             if (column.type.hasCustomValidation) {
                  ConditionalFormattingHelper.clearConditionalFormatting(worksheet, excelColIndex, startRow, startRow); // Use dummy range if needed
             }
         }
    }
    // --- END NEW HELPER ---

    // Helper method in SheetDataStore (should exist or be added)
    applyColumnValidation(worksheet, columnIndex, validation, skipHeader = true) {
        const startRow = skipHeader ? 2 : 1;
        // --- MODIFICATION: Iterate based on model row count --- 
        const numDataRows = this.currentSheet?.rows?.size || 0;
        const endRow = numDataRows + 1; // +1 for header offset, so loop goes up to last data row index
        // --- END MODIFICATION ---
        for (let rowNumber = startRow; rowNumber <= endRow; rowNumber++) {
            const cell = worksheet.getCell(rowNumber, columnIndex);
            // Check if cell exists before applying validation
            if (cell) {
                // Use spread syntax for safety, ensuring a new object is assigned
                cell.dataValidation = { ...validation }; 
            }
        }
    }
}